/**
 * Reward Background Service
 * Handles API calls for reward background system
 */

(function() {
  window.rewardBackgroundService = {
  baseUrl: '/api/reward_backgrounds',

  /**
   * Get all reward backgrounds
   * @param {Object} filters - Optional filters
   * @param {string} filters.reward_type - Filter by type (quest, rank, shop, chest)
   * @param {boolean} filters.is_active - Filter by active status
   * @param {boolean} filters.is_featured - Filter by featured status
   * @param {string} filters.twitch_id - User ID to check claim status
   * @returns {Promise}
   */
  get(filters = {}) {
    const params = new URLSearchParams(filters);
    return fetch(`${this.baseUrl}/get.php?${params.toString()}`)
      .then(response => response.json());
  },

  /**
   * Get reward backgrounds by specific type
   * @param {string} type - Reward type (quest, rank, shop, chest)
   * @param {string} twitchId - Optional user ID to check claim status
   * @returns {Promise}
   */
  getByType(type, twitchId = null) {
    const filters = { reward_type: type };
    if (twitchId) {
      filters.twitch_id = twitchId;
    }
    return this.get(filters);
  },

  /**
   * Get shop items (backgrounds purchasable with currency)
   * @param {string} twitchId - Optional user ID to check claim status
   * @returns {Promise}
   */
  getShopItems(twitchId = null) {
    return this.getByType('shop', twitchId);
  },

  /**
   * Get quest rewards
   * @param {string} twitchId - Optional user ID to check claim status
   * @returns {Promise}
   */
  getQuestRewards(twitchId = null) {
    return this.getByType('quest', twitchId);
  },

  /**
   * Get rank rewards
   * @param {string} twitchId - Optional user ID to check claim status
   * @returns {Promise}
   */
  getRankRewards(twitchId = null) {
    return this.getByType('rank', twitchId);
  },

  /**
   * Get chest rewards
   * @param {string} twitchId - Optional user ID to check claim status
   * @returns {Promise}
   */
  getChestRewards(twitchId = null) {
    return this.getByType('chest', twitchId);
  },

  /**
   * Get featured rewards
   * @param {string} twitchId - Optional user ID to check claim status
   * @returns {Promise}
   */
  getFeatured(twitchId = null) {
    const filters = { is_featured: true };
    if (twitchId) {
      filters.twitch_id = twitchId;
    }
    return this.get(filters);
  },

  /**
   * Save (create or update) a reward background
   * @param {Object} rewardData - Reward background data
   * @returns {Promise}
   */
  save(rewardData) {
    return fetch(`${this.baseUrl}/save.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(rewardData)
    }).then(response => response.json());
  },

  /**
   * Delete a reward background
   * @param {string} id - Reward background ID
   * @returns {Promise}
   */
  delete(id) {
    return fetch(`${this.baseUrl}/delete.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id })
    }).then(response => response.json());
  },

  /**
   * Claim a reward background
   * @param {string} rewardBackgroundId - Reward background ID
   * @param {Object} options - Additional options
   * @param {string} options.claim_method - How it was claimed
   * @param {string} options.transaction_id - Transaction reference
   * @param {number} options.currency_paid - Amount paid (for shop items)
   * @returns {Promise}
   */
  claim(rewardBackgroundId, options = {}) {
    return fetch(`${this.baseUrl}/claim.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        reward_background_id: rewardBackgroundId,
        ...options
      })
    }).then(response => response.json());
  },

  /**
   * Claim a shop item (purchase with currency)
   * @param {string} rewardBackgroundId - Reward background ID
   * @param {string} currencyType - Type of currency
   * @param {number} currencyAmount - Amount to pay
   * @returns {Promise}
   */
  purchaseShopItem(rewardBackgroundId, currencyType, currencyAmount) {
    return this.claim(rewardBackgroundId, {
      claim_method: 'shop_purchase',
      currency_paid: currencyAmount
    });
  },

  /**
   * Claim a quest reward
   * @param {string} rewardBackgroundId - Reward background ID
   * @param {string} questId - Quest ID
   * @returns {Promise}
   */
  claimQuestReward(rewardBackgroundId, questId) {
    return this.claim(rewardBackgroundId, {
      claim_method: 'quest_complete',
      transaction_id: questId
    });
  },

  /**
   * Claim a rank reward
   * @param {string} rewardBackgroundId - Reward background ID
   * @param {string} rankName - Rank name
   * @returns {Promise}
   */
  claimRankReward(rewardBackgroundId, rankName) {
    return this.claim(rewardBackgroundId, {
      claim_method: 'rank_up',
      transaction_id: rankName
    });
  },

  /**
   * Claim a chest reward
   * @param {string} rewardBackgroundId - Reward background ID
   * @param {string} chestId - Chest ID
   * @returns {Promise}
   */
  claimChestReward(rewardBackgroundId, chestId) {
    return this.claim(rewardBackgroundId, {
      claim_method: 'chest_open',
      transaction_id: chestId
    });
  },

  /**
   * Helper to format currency display
   * @param {string} currencyType - Type of currency
   * @param {number} amount - Amount
   * @returns {string}
   */
  formatCurrency(currencyType, amount) {
    const currencySymbols = {
      barks: '🪙',
      gems: '💎',
      points: '⭐',
      gold: '🏅'
    };

    const symbol = currencySymbols[currencyType] || '';
    return `${symbol} ${amount.toLocaleString()}`;
  },

  /**
   * Helper to get reward type display name
   * @param {string} rewardType - Type of reward
   * @returns {string}
   */
  getRewardTypeDisplay(rewardType) {
    const displayNames = {
      quest: 'Quest Reward',
      rank: 'Rank Reward',
      shop: 'Shop Item',
      chest: 'Chest Drop'
    };
    return displayNames[rewardType] || rewardType;
  },

  /**
   * Helper to check if reward is available
   * @param {Object} reward - Reward object
   * @returns {boolean}
   */
  isRewardAvailable(reward) {
    // Check active status
    if (!reward.is_active) return false;

    // Check date range
    const now = new Date();
    if (reward.start_date) {
      const startDate = new Date(reward.start_date);
      if (now < startDate) return false;
    }
    if (reward.end_date) {
      const endDate = new Date(reward.end_date);
      if (now > endDate) return false;
    }

    // Check max claims
    if (reward.max_claims !== null && reward.current_claims >= reward.max_claims) {
      return false;
    }

    return true;
  }
};
})();
